import Image from 'next/image';
import Link from 'next/link';
import { Trans, useTranslation } from 'next-i18next';
import { useCallback, useEffect, useState } from 'react';
import { FaDiscord } from 'react-icons/fa6';

import Button from '@/common/button';
import { GAMERS_DAYS_CAMPAIGNS } from '@/constants/campaigns/gamers-day';
import { useAuth } from '@/hooks/use-auth';
import { useFeatureToggle } from '@/hooks/use-feature-toggle';

interface TrialHeroProps {
  daysTrial?: number;
  pathVideoWithOutNoPing: string;
  pathVideoWithNoPing: string;
  customTrial?: number | null;
  onModalSignUp?: (state: boolean) => void;
  isLoaderDone?: boolean;
  isDiscordTrial?: boolean;
}

export default function TrialHero({ daysTrial = 7, pathVideoWithNoPing, pathVideoWithOutNoPing, customTrial, onModalSignUp, isLoaderDone, isDiscordTrial = false }: TrialHeroProps) {
  const { t } = useTranslation('components/trial/hero');
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useAuth();

  useEffect(() => {
    if (isLoaderDone && loading) {
      setLoading(false);
    }
  }, [isLoaderDone, loading]);

  const handleButtonCustomTrial = useCallback(() => {
    if (!onModalSignUp) return;
    if (user) {
      setLoading(true);
    }
    onModalSignUp(true);
  }, [onModalSignUp, user]);

  const { isEnabled: isEnabledGamersDay } = useFeatureToggle({ key: GAMERS_DAYS_CAMPAIGNS, autoFetch: true });

  const DefineButton = useCallback(() => {
    if (customTrial && !isDiscordTrial) {
      return (
        <div className='space-y-2'>
          <Button variant='primary' className='mt-5 py-4 transition-opacity hover:opacity-80' onClick={handleButtonCustomTrial} loading={loading}>
            <Trans values={{ days: daysTrial }}>{t('trial.button_days_free')}</Trans>
          </Button>
          <p className='text-base font-normal text-green-300'>{t('trial.no_credit_card')}</p>
        </div>
      );
    }

    if (isDiscordTrial) {
      return (
        <div className='space-y-2'>
          <Link className='click_days_free_discord mt-5 flex items-stretch justify-center gap-2 rounded-xl bg-[#5865F2] py-4 text-center text-sm font-bold uppercase text-white transition-opacity hover:opacity-80 mobile:w-fit mobile:px-10' href={process.env.NEXT_PUBLIC_DISCORD_URL_TRIAL ?? ''} target='_blank' rel='noopener noreferrer'>
            <span className='hidden mobile:flex'>
              <FaDiscord size={24} />
            </span>
            <span className='px-2'>
              <Trans values={{ days: daysTrial }}>{t('trial.button_days_free_discord')}</Trans>
            </span>
          </Link>
          <p className='text-base font-normal text-green-300'>{t('trial.no_credit_card')}</p>
        </div>
      );
    }

    return (
      <div className='flex flex-col gap-2.5'>
        <a href='#form' className='lex mt-5 flex w-full items-center justify-center rounded-xl bg-green-300 py-4 text-center text-black transition-opacity hover:opacity-80 mobile:w-fit mobile:px-10'>
          <span className='font-bold uppercase'>
            <Trans values={{ days: daysTrial }}>{t('trial.button_days_free')}</Trans>
          </span>
        </a>
        {isEnabledGamersDay && <p className='text-base font-normal text-green-300'>{t('trial.no_credit_card')}</p>}
      </div>
    );
  }, [customTrial, isDiscordTrial, daysTrial, loading, handleButtonCustomTrial, t, isEnabledGamersDay]);

  return (
    <div className='flex items-center justify-center'>
      <div className='flex w-full max-w-[1400px] flex-col items-center gap-10 p-5 xl:flex-row xl:py-20'>
        <div className='mb-10'>
          <h1 className='max-w-[652px] text-6xl font-bold leading-[1.1] text-white'>{t('trial.title')}</h1>
          <p className='max-w-[559px] pt-6 text-base font-[500] leading-[1.4] text-[#616C7B]'>{t('trial.description')}</p>
          <DefineButton />
        </div>
        <div className='mx-auto mt-10 w-full max-w-[650px] ipad:mx-0 laptop:mt-4'>
          <div className='flex flex-col gap-2'>
            <div className='flex items-end gap-4'>
              <div className='flex flex-col gap-2'>
                <p className='font-bold text-[#FF002A]'>{t('trial.without_noping')}</p>
                <video preload='none' autoPlay muted loop aria-hidden='true' className='w-full max-w-[450px] rounded-xl border border-[#FF002A]'>
                  <source src={pathVideoWithOutNoPing} type='video/mp4' />
                </video>
              </div>
              <div className='ms-auto flex items-end gap-4'>
                <Image src='/assets/images/novo-logo-verde-branco.png' alt='Logo NoPing' width={120} height={32} className='flex' />
                <span className='relative -start-2 hidden font-bold text-[#AEF55C] xl:flex'>ON</span>
              </div>
            </div>
            <div className='flex w-full justify-end'>
              <video preload='none' autoPlay muted loop aria-hidden='true' className='w-full max-w-[450px] rounded-xl border border-green-300'>
                <source src={pathVideoWithNoPing} type='video/mp4' />
              </video>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
